/**
 * Reads a note's YAML frontmatter as metadata rather than prose.
 *
 * The client owns this derivation rather than consuming a pre-split body from
 * the server, because a note's bytes reach the reader by three routes: pulled
 * from the server, restored from the offline cache, and typed into an import.
 * Only the last two never touch a sync payload, and a note that renders its
 * `---` fence as a paragraph on those routes is the same defect the server
 * split would have fixed on one route only.
 *
 * It also means the pushed body stays byte-identical to the stored file. If the
 * payload's `body` arrived already stripped, the next push would write the note
 * back without its frontmatter and silently destroy it.
 *
 * The title is not derived here. The Rust core owns that rule
 * (`rv_model::document_title`), and this module calls its WASM export; the
 * server's TypeScript mirror is pinned to the same case table. The reader
 * needs a title synchronously and before any worker round-trip, so this module
 * loads the core at evaluation time in the document realm. The worker loads
 * and registers it during its observable initialization instead: a top-level
 * worker await would prevent its message handler from reporting load failures.
 * Tests register the shipped bundle through `registerTitleCore`.
 */

import { loadCore } from "./core.js";

/** The slice of the core a title needs. */
export interface TitleCore {
  documentTitle(body: string, path: string): string;
}

let titleCore: TitleCore | undefined;
let coreFailure: Error | undefined;

/** Supplies the core to derive titles from, for a realm that loaded it itself. */
export function registerTitleCore(core: TitleCore): void {
  titleCore = core;
  coreFailure = undefined;
}

if (typeof document !== "undefined") {
  try {
    titleCore = (await loadCore()).module;
  } catch (error) {
    coreFailure = error instanceof Error ? error : new Error(String(error));
  }
}

/** A note's frontmatter block split away from the markdown it prefixes. */
export interface NoteDocument {
  /** Frontmatter title, else the first H1, else a name derived from the path. */
  readonly title: string;
  /** The markdown below the frontmatter block, safe to render as prose. */
  readonly content: string;
  /** Parsed frontmatter keys. Empty when there is none, or none was readable. */
  readonly metadata: ReadonlyMap<string, string>;
}

/**
 * A frontmatter block only counts when it opens the file. A `---` appearing
 * lower down is a horizontal rule or a YAML block quoted inside prose, and
 * treating it as metadata would eat real body text.
 */
const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/;

export function splitFrontmatter(markdown: string): { block: string | null; content: string } {
  const match = FRONTMATTER.exec(markdown);
  if (match === null) return { block: null, content: markdown };
  return { block: match[1] ?? "", content: markdown.slice(match[0].length) };
}

function unquote(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && /^(".*"|'.*')$/s.test(trimmed)) return trimmed.slice(1, -1);
  return trimmed;
}

function formatList(items: readonly string[]): string {
  return items.map(unquote).filter(Boolean).join(", ");
}

/**
 * Reads the subset of YAML that vault notes actually use: scalars, inline
 * `[a, b]` lists, and block lists of `- item` lines.
 *
 * Anything it cannot read is skipped rather than thrown. Malformed frontmatter
 * is data — a note with a stray tab or an unclosed quote must still be
 * readable, showing whatever keys did parse.
 */
export function parseFrontmatter(block: string | null): ReadonlyMap<string, string> {
  const fields = new Map<string, string>();
  if (block === null) return fields;
  let listKey: string | null = null;
  let listItems: string[] = [];
  const flush = (): void => {
    if (listKey !== null && listItems.length > 0) fields.set(listKey, formatList(listItems));
    listKey = null;
    listItems = [];
  };
  for (const line of block.split(/\r?\n/)) {
    const item = /^\s*-\s+(.*)$/.exec(line);
    if (item !== null && listKey !== null) {
      listItems.push(item[1] ?? "");
      continue;
    }
    const pair = /^([A-Za-z0-9_.-]+)\s*:\s*(.*)$/.exec(line);
    if (pair === null) continue;
    flush();
    const key = (pair[1] ?? "").toLowerCase();
    const raw = (pair[2] ?? "").trim();
    if (raw.length === 0) {
      listKey = key;
      continue;
    }
    const inline = /^\[(.*)\]$/s.exec(raw);
    if (inline !== null) {
      fields.set(key, formatList((inline[1] ?? "").split(",")));
      continue;
    }
    fields.set(key, unquote(raw));
  }
  flush();
  return fields;
}

/**
 * Names a note the way the document names itself, by asking the core.
 *
 * A vault of AI-written notes has filenames shaped like slugs, so the filename
 * is the last resort and never the first choice. The list and the reader both
 * call this: deriving a title twice by two rules puts two names for one note on
 * a single screen.
 */
export function noteTitle(markdown: string, path: string): string {
  if (!titleCore) {
    throw coreFailure ?? new Error("RecallVault's secure core could not load: no core registered");
  }
  return titleCore.documentTitle(markdown, path);
}

/** The frontmatter keys worth showing above a note, in the order they read. */
const HEADER_KEYS = ["tags", "status", "created", "updated"] as const;

export interface MetadataField {
  readonly key: string;
  readonly value: string;
}

/**
 * Selects the few keys a reader benefits from seeing. Dumping every key turns
 * the header back into the wall of YAML this change exists to remove.
 */
export function headerMetadata(metadata: ReadonlyMap<string, string>): readonly MetadataField[] {
  return HEADER_KEYS
    .map((key) => ({ key, value: metadata.get(key)?.trim() ?? "" }))
    .filter(({ value }) => value.length > 0);
}

/** Everything the reader needs from one note's raw markdown, derived once. */
export function readNote(markdown: string, path: string): NoteDocument {
  const { block, content } = splitFrontmatter(markdown);
  const metadata = parseFrontmatter(block);
  return { title: noteTitle(markdown, path), content, metadata };
}
